import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Redirect } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import { FaChalkboard, FaPlus } from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/class-settings-modals.css";

//Created By Cursor
const MdlAddNewClass = (props) => {
  const token = localStorage.getItem("token");
  const loggedIn = token !== null;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState(true);
  const [mClassBoard, setMClassBoard] = useState([]);
  const [mClassMedium, setMClassMedium] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState("");
  const [selectedMediumId, setSelectedMediumId] = useState("");
  const [className, setClassName] = useState("");
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [mStaffList, setMStaffList] = useState([]);
  const [affiliations, setAffiliations] = useState([]);
  const [selectedAffiliationId, setSelectedAffiliationId] = useState("");

  //Created By Cursor
  const resolveSessionId = useCallback(() => {
    return (
      props.sessionId ||
      SessionManager.getCurrentSession()?.[0]?.session_id ||
      ""
    );
  }, [props.sessionId]);

  const HandleClose = () => {
    setShow(false);
    if (typeof props.actionClose === "function") {
      props.actionClose();
    }
  };

  //Created By Cursor
  const handleSubmit = async () => {
    if (!selectedBoardId || !selectedMediumId || !className.trim()) {
      myToast.notify_warning("Please fill all required fields");
      return;
    }

    const SESSION_ID = resolveSessionId();
    if (!SESSION_ID) {
      myToast.notify_warning("Session is missing. Please select a session.");
      return;
    }

    setSaving(true);
    try {
      const SCHOOL_CODE = SessionManager.getSchoolCode();
      const mStaffData = SessionManager.getStaffDetails();
      const STAFF_ID = mStaffData?.staff_id || "";
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}AddNewClass.php`;

      const response = await axios.get(URL, {
        params: {
          staff_id: STAFF_ID,
          session_id: SESSION_ID,
          class_name: className.trim(),
          board_id: selectedBoardId,
          medium_id: selectedMediumId,
          school_code: SCHOOL_CODE,
          affiliation_id: selectedAffiliationId || undefined,
        },
        timeout: 10000,
      });

      if (response.data?.api_status === "OK") {
        myToast.notify_success("Class added successfully!");
        setClassName("");
        setSelectedStaffId("");
        if (typeof props.action2 === "function") {
          props.action2();
        } else {
          HandleClose();
        }
      } else {
        myToast.notify_error(
          response.data?.api_remark || "Failed to add class"
        );
      }
    } catch (error) {
      console.error("Error adding class:", error);
      myToast.notify_error("Something went wrong while adding class");
    } finally {
      setSaving(false);
    }
  };

  //Created By Cursor
  const getBoardAndMedium = useCallback(() => {
    const SESSION_ID = resolveSessionId();
    if (!SESSION_ID) {
      myToast.notify_error("Session data not available");
      return;
    }

    setLoading(true);
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getClassWithStudentCount.php`;

    axios
      .get(URL, {
        params: {
          school_code: SCHOOL_CODE,
          session_id: SESSION_ID,
        },
      })
      .then((res) => res.data)
      .then((response) => {
        setLoading(false);
        if (response.api_status === "OK") {
          const boards = response.data.board || [];
          const mediums = response.data.medium || [];
          setMClassBoard(boards);
          setMClassMedium(mediums);
          if (boards[0]?.board_id) setSelectedBoardId(boards[0].board_id);
          if (mediums[0]?.medium_id) setSelectedMediumId(mediums[0].medium_id);
        } else {
          myToast.notify_warning(response.api_remark);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log("Error fetching board and medium:", error);
        myToast.notify_error("Failed to load board and medium data");
      });
  }, [resolveSessionId]);

  const getStaff = useCallback(() => {
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getStaff.php`;

    axios
      .get(URL, { params: { school_code: SCHOOL_CODE } })
      .then((res) => res.data)
      .then((response) => {
        if (response.api_status === "OK") {
          setMStaffList(response.staff || []);
        }
      })
      .catch((error) => {
        console.log("Error fetching staff:", error);
      });
  }, []);

  //Created By Cursor — load affiliations for class mapping (independent of board/medium)
  const getAffiliations = useCallback(() => {
    const SESSION_ID = resolveSessionId();
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    if (!SESSION_ID || !SCHOOL_CODE) return;
    axios
      .get(
        `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getSchoolAffiliations.php`,
        { params: { school_code: SCHOOL_CODE, session_id: SESSION_ID } }
      )
      .then((res) => res.data)
      .then((response) => {
        if (response.api_status === "OK") {
          const list = response.data || [];
          setAffiliations(list);
          const primary =
            list.find((a) => Number(a.is_primary) === 1) || list[0];
          if (primary?.affiliation_id) {
            setSelectedAffiliationId(primary.affiliation_id);
          }
        }
      })
      .catch(() => {});
  }, [resolveSessionId]);

  useEffect(() => {
    getBoardAndMedium();
    getStaff();
    getAffiliations();
  }, [getBoardAndMedium, getStaff, getAffiliations]);

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <Modal
      show={show}
      onHide={HandleClose}
      animation={false}
      size="md"
      centered
      dialogClassName="cls-mdl-root"
      aria-labelledby="add-class-title"
    >
      <Modal.Header closeButton>
        <div>
          <Modal.Title id="add-class-title">
            <FaChalkboard />
            Add New Class
          </Modal.Title>
          <span className="cls-mdl-sub">
            Create a class for the selected board and medium
          </span>
        </div>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <MyLoader />
        ) : (
          <div className="cls-mdl-panel">
            <div className="cls-mdl-grid">
              <label className="cls-mdl-field">
                <span>Board *</span>
                <select
                  value={selectedBoardId}
                  onChange={(e) => setSelectedBoardId(e.target.value)}
                  disabled={saving}
                >
                  <option value="">Select Board</option>
                  {mClassBoard.map((item) => (
                    <option key={item.board_id} value={item.board_id}>
                      {item.board_name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="cls-mdl-field">
                <span>Medium *</span>
                <select
                  value={selectedMediumId}
                  onChange={(e) => setSelectedMediumId(e.target.value)}
                  disabled={saving}
                >
                  <option value="">Select Medium</option>
                  {mClassMedium.map((item) => (
                    <option key={item.medium_id} value={item.medium_id}>
                      {item.medium_name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="cls-mdl-field">
                <span>Class Name *</span>
                <input
                  type="text"
                  required
                  placeholder="e.g. Play Group"
                  value={className}
                  onChange={(e) => setClassName(e.target.value)}
                  disabled={saving}
                />
              </label>

              <label className="cls-mdl-field">
                <span>Affiliation</span>
                <select
                  value={selectedAffiliationId}
                  onChange={(e) => setSelectedAffiliationId(e.target.value)}
                  disabled={saving}
                >
                  <option value="">Select Affiliation (optional)</option>
                  {affiliations.map((item) => (
                    <option
                      key={item.affiliation_id}
                      value={item.affiliation_id}
                    >
                      {item.affiliation_name ||
                        item.school_name ||
                        item.affiliation_id}
                      {item.affiliation_no ? ` · ${item.affiliation_no}` : ""}
                    </option>
                  ))}
                </select>
              </label>

              <label className="cls-mdl-field">
                <span>Class Teacher (Optional)</span>
                <select
                  value={selectedStaffId}
                  onChange={(e) => setSelectedStaffId(e.target.value)}
                  disabled={saving}
                >
                  <option value="">Select Teacher</option>
                  {mStaffList.map((item) => (
                    <option key={item.staff_id} value={item.staff_id}>
                      {item.staff_name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          className="cls-mdl-btn-ghost"
          onClick={HandleClose}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          className="cls-mdl-btn-cta"
          onClick={handleSubmit}
          disabled={loading || saving}
        >
          <FaPlus style={{ marginRight: 6 }} />
          {saving ? "Saving..." : "Add Class"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MdlAddNewClass;
